import {
  isDefinedArray,
  isDefinedString,
  isPositiveNumber
} from "et3-type-guards";
import { memoize, orderBy } from "lodash";
import { computed, observable, runInAction } from "mobx";

import { apiCallGetAdminEvents } from "../../../api/auditor-api/api-call-get-admin-events";
import { apiCallUpdateAuditAdminEvents } from "../../../api/auditor-api/api-call-update-audit-admin-events";
import { getSpecificDateWithDays } from "../../../core/et3-date";
import { TDataRow, TEventsList } from "../../../Types";
import getApproveRejectStore from "../../approval-reject-store";
import getCleanPoolStore from "../../core/core-clean-pool";
import getCorePersonalStore from "../../core/core-personal-store";
import getCorePopUpStore from "../../core/core-popup-store";
import getCoreUIStore from "../../core/core-ui-store";
import getTBWStore from "../../developer-store/tbw-store";
import i18n from "../../../constants/i18n";
import { parseConversationData } from "../../../helpers/et3-conversation-parse";
import { TEt3PickerSingleValue } from "../../../TypesNEW";
import {
  reducerToEmptyStringIfUndefined,
  reducerToZeroIfUndefined
} from "../../../helpers/et3-picker-data-helper";

class AuditStore {
  adminInput = observable.box<TEt3PickerSingleValue>({
    value: "",
    label: i18n.get("CHOOSE")
  });
  dateMinInput = observable.box<Date>(
    getSpecificDateWithDays({ daysSubtracted: 1, daysAdded: 0 })
  );
  dateMaxInput = observable.box<Date>(
    getSpecificDateWithDays({ daysSubtracted: 0, daysAdded: 1 })
  );
  limitInput = observable.box<TEt3PickerSingleValue>({
    label: "50",
    value: "50"
  });
  typeInputAudit = observable.box<TEt3PickerSingleValue>({
    label: "Approve/Reject Description",
    value: "approval_description"
  });
  subscriptionInput = observable.box<TEt3PickerSingleValue>({
    label: "",
    value: ""
  });
  genderInput = observable.box<TEt3PickerSingleValue>({
    label: "",
    value: ""
  });
  actionTakenInput = observable.box<TEt3PickerSingleValue>({
    label: "",
    value: ""
  });
  searchedTypeInputAudit = observable.box<string>("approval_description");
  adminEventsData = observable.box<{
    eventsList: TEventsList;
    eventsCount: TDataRow[];
    totalEvents: number;
  }>({ eventsList: [], eventsCount: [], totalEvents: 0 });
  adminReportEventsData = observable.box<{
    eventsList: TEventsList;
    eventsCount: TDataRow[];
    totalEvents: number;
  }>({ eventsList: [], eventsCount: [], totalEvents: 0 });

  eventsListOrdered = computed(() => {
    const eventsList = orderBy(
      this.adminEventsData.get().eventsList,
      ["date"],
      ["desc"]
    );
    return eventsList;
  });

  enableCleanPool = computed(() => {
    const currentEventData = this.adminEventsData.get();
    const currentReportData = this.adminReportEventsData.get();
    const isDataExist =
      isDefinedArray(currentEventData.eventsList) ||
      isDefinedArray(currentReportData.eventsList);

    const isExistingDatasBelongToSearchingType =
      //here we are checking if the existing data belongs to the chosen type in the form. Because reports are taking there data
      //from another array of data so it should be not related to the others since the others take there data from the same array
      this.typeInputAudit.get().value === this.searchedTypeInputAudit.get() ||
      (this.typeInputAudit.get().value !== "report" &&
        this.searchedTypeInputAudit.get() !== "report");
    const enableCleanPool = isDataExist && isExistingDatasBelongToSearchingType;
    return enableCleanPool;
  });

  setTypeInputAudit = (type: TEt3PickerSingleValue) => {
    runInAction(() => {
      this.typeInputAudit.set(type);
    });
  };

  setSubscriptionInput = (subscription: TEt3PickerSingleValue) => {
    runInAction(() => {
      this.subscriptionInput.set(subscription);
    });
  };

  setActionTakenInput = (actionTaken: TEt3PickerSingleValue) => {
    runInAction(() => {
      this.actionTakenInput.set(actionTaken);
    });
  };

  setGenderInput = (gender: TEt3PickerSingleValue) => {
    runInAction(() => {
      this.genderInput.set(gender);
    });
  };

  setLimitInput = (value: TEt3PickerSingleValue) => {
    runInAction(() => {
      this.limitInput.set(value);
    });
  };

  setSearchedTypeInputAudit = (type: string) => {
    runInAction(() => {
      this.searchedTypeInputAudit.set(type);
    });
  };

  resetAll = () => {
    runInAction(() => {
      this.adminEventsData.set({
        eventsList: [],
        eventsCount: [],
        totalEvents: 0
      });
      this.adminReportEventsData.set({
        eventsList: [],
        eventsCount: [],
        totalEvents: 0
      });
    });
  };

  getEvents = async () => {
    this.resetAll();
    const supervisorId = getCorePersonalStore().user.get().id;
    const definedAdminID = reducerToZeroIfUndefined(
      parseInt(this.adminInput.get().value, 10)
    );
    const definedLimit = reducerToZeroIfUndefined(
      parseInt(this.limitInput.get().value, 10)
    );
    const type = this.typeInputAudit.get().value;
    const subscription = reducerToEmptyStringIfUndefined(
      this.subscriptionInput.get().value
    );
    const gender = reducerToEmptyStringIfUndefined(
      this.genderInput.get().value
    );
    const actionTaken = reducerToEmptyStringIfUndefined(
      this.actionTakenInput.get().value
    );
    if (!isPositiveNumber(definedAdminID) || !isPositiveNumber(definedLimit)) {
      return;
    }
    getCoreUIStore().setIsLoading("AUDITOR_AUDIT", true);
    if (type !== "report") {
      const { data, status, code } = await apiCallGetAdminEvents({
        supervisorId,
        adminId: definedAdminID,
        dateMin: this.dateMinInput.get(),
        dateMax: this.dateMaxInput.get(),
        type,
        subscription,
        gender,
        actionTaken,
        limit: definedLimit
      });
      getCoreUIStore().setIsLoading("AUDITOR_AUDIT", false);
      if (status === "OK") {
        this.adminEventsData.set(data);
        this.setSearchedTypeInputAudit(this.typeInputAudit.get().value);
      } else {
        getCorePopUpStore().showAlertMessage(code);
      }
    }

    if (type === "report") {
      const { data, status, code } = await apiCallGetAdminEvents({
        supervisorId,
        adminId: definedAdminID,
        dateMin: this.dateMinInput.get(),
        dateMax: this.dateMaxInput.get(),
        type,
        subscription,
        gender,
        actionTaken,
        limit: definedLimit
      });
      getCoreUIStore().setIsLoading("AUDITOR_AUDIT", false);
      if (status === "OK") {
        data.eventsList.map((report) => {
          report.reportData.conversation = parseConversationData(
            report.reportData.conversation
          );
        });
        this.adminReportEventsData.set(data);
        this.setSearchedTypeInputAudit(this.typeInputAudit.get().value);
      } else {
        getCorePopUpStore().showAlertMessage(code);
      }
    }
  };

  cleanAuditPool = async () => {
    getCleanPoolStore().cleanPool({
      currentData: this.adminEventsData.get(),
      page: "AUDITOR_AUDIT",
      poolType: "AUDITOR_AUDIT_EVENTS",
      successVoidFunction: () => this.resetAll()
    });
  };

  takeAuditAction = async ({
    actionType,
    userID,
    userUUID,
    eventID,
    eventType,
    imageID
  }: {
    actionType: "minor" | "major" | "no_issue";
    userID: number;
    userUUID: string;
    eventID: number;
    eventType: string;
    imageID: string;
  }) => {
    const adminId = getCorePersonalStore().user.get().id;
    const hasIssue = actionType === "major" || actionType === "minor";
    if (this.typeInputAudit.get().value === "report") {
      const filteredEvents = this.adminReportEventsData
        .get()
        .eventsList.filter((item) => item.id !== eventID);
      this.adminReportEventsData.set({
        ...this.adminReportEventsData.get(),
        eventsList: filteredEvents
      });
    }
    const isApprovedDescription =
      eventType === "UPDATE_DESCRIPTION_APPROVE" ||
      eventType === "APPROVE_DESCRIPTION" ||
      eventType === "APPROVE_PEER_DESCRIPTION";
    const isApprovedPicture =
      eventType === "UPDATE_PICTURE_APPROVE" || eventType === "APPROVE_PICTURE";

    switch (this.typeInputAudit.get().value) {
      case "approval_description":
        if (hasIssue && isApprovedDescription) {
          getApproveRejectStore().rejectUserDescription(
            -1,
            userID,
            userUUID,
            "AUDITOR_AUDIT",
            "self_description",
            false,
            ""
          );
        }
        break;
      case "approval_peer_description":
        if (hasIssue && isApprovedDescription) {
          getApproveRejectStore().rejectUserDescription(
            -1,
            userID,
            userUUID,
            "AUDITOR_AUDIT",
            "peer_description",
            false,
            ""
          );
        }
        break;
      case "approval_picture":
        if (hasIssue && isApprovedPicture) {
          if (isDefinedString(`${imageID}`)) {
            getApproveRejectStore().rejectUserPicture(
              userID,
              userUUID,
              "AUDITOR_AUDIT",
              `${imageID}`,
              false,
              ""
            );
          } else {
            getCorePopUpStore().showAlertMessageWithExtraData(
              "USER_ID_PICTURE_REJECT",
              `${userID}`
            );
          }
        }
        break;
      default:
        break;
    }
    try {
      const reason_ids = getTBWStore().getTWBValuesArray();
      const proof = getTBWStore().auditEventProof.get();
      getCoreUIStore().setIsLoading("AUDITOR_AUDIT", true);
      const { status, code } = await apiCallUpdateAuditAdminEvents({
        adminId,
        issueType: actionType,
        eventID,
        type: this.typeInputAudit.get().value,
        reason_ids,
        proof
      });
      getCoreUIStore().setIsLoading("AUDITOR_AUDIT", false);
      if (status === "ERROR") {
        getCorePopUpStore().showAlertMessage(code);
      } else {
        if (this.typeInputAudit.get().value !== "report") {
          const filteredEvents = this.adminEventsData
            .get()
            .eventsList.filter((item) => item.id !== eventID);
          this.adminEventsData.set({
            ...this.adminEventsData.get(),
            eventsList: filteredEvents
          });
        }
      }
      getTBWStore().resetTBWPopUp();
    } catch (err) {
      getCoreUIStore().setIsLoading("AUDITOR_AUDIT", false);
    }
  };
  isGettingEventsDisabled = () => {
    const definedAdminID = reducerToZeroIfUndefined(
      parseInt(this.adminInput.get().value, 10)
    );
    const eventType = this.typeInputAudit.get().value;
    const actionTaken = this.actionTakenInput.get().value;
    const isRestrictedEvent =
      eventType !== "report" && eventType !== "investigation_cases";
    const hasRestrictedFilter = isDefinedString(actionTaken);
    const isEnabled = isPositiveNumber(definedAdminID);

    return !isEnabled || (isRestrictedEvent && hasRestrictedFilter);
  };
  setAdmin = ({ admin }: { admin: TEt3PickerSingleValue }) => {
    runInAction(() => {
      this.adminInput.set(admin);
    });
  };
}

const getAuditStore = memoize(
  () => new AuditStore(),
  () => 1
);
export default getAuditStore;
